import { Autocomplete, Box, Button, Card, CardContent, Chip, IconButton, MenuItem, Popover, TextField, Tooltip, Typography, type TextFieldProps } from "@mui/material"
import { ArrowDown, ArrowUp, Info, Plus, Trash2 } from "lucide-react"
import { useState, type JSX } from "react"

import { PreShipValueInput } from "@crm-src/components/pre-ship/pre-ship-value-input"
import { move, newLine, typeOf, VALUE_TYPES, type PreShipLine, type PreShipSection, type PreShipValueType } from "@crm-src/lib/pre-ship-api"

export interface PreShipSectionEditorProps {
  section: PreShipSection
  index: number
  count: number
  editable: boolean
  onChange: (next: PreShipSection) => void
  onMove: (delta: -1 | 1) => void
  /** Asks the page to confirm and remove the section. */
  onRemove: () => void
  /** crm-api's INVALID_VALUES from the last save, by row id. */
  serverErrors?: Record<string, string>
}

/** Item (with its ⓘ help) │ Value │ value type │ line actions. */
const GRID = { xs: "1fr", md: "minmax(0,1.3fr) minmax(0,1.6fr) 150px 140px" }

/**
 * One section of a pre-ship questionnaire, as staff edit it — the
 * spreadsheet, on screen (CEO, 2026-10-03): a title, then one line per row —
 * what it is, and the value. "Add a line" under every section. A line the
 * CUSTOMER added in the support portal wears a "Customer" chip.
 *
 * Feedback, 2026-10-23: the Description column became HELP TEXT behind an ⓘ
 * next to the item (staff write it in a popover; the customer reads it as a
 * tooltip), and every line has a VALUE TYPE — the select beside the value —
 * which shapes the value input in both portals and which crm-api enforces.
 * A "List of values" line shows its allowed values under the line.
 */
export function PreShipSectionEditor({ section, index, count, editable, onChange, onMove, onRemove, serverErrors = {} }: PreShipSectionEditorProps): JSX.Element {
  const rows = section.rows
  const setRow = (row: PreShipLine) => onChange({ ...section, rows: rows.map((r) => (r.id === row.id ? row : r)) })

  return (
    <Card data-cy={`preship-section-${index}`}>
      <CardContent sx={{ display: "grid", gap: 1.25 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            size="small"
            fullWidth
            disabled={!editable}
            value={section.title}
            placeholder="Section title"
            onChange={(e) => onChange({ ...section, title: e.target.value })}
            inputProps={{ "aria-label": "Section title", "data-cy": "preship-section-title", style: { fontWeight: 600 } }}
          />
          {editable && (
            <>
              <Tooltip title="Move section up">
                <span>
                  <IconButton size="small" aria-label="Move section up" disabled={index === 0} onClick={() => onMove(-1)}>
                    <ArrowUp size={16} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move section down">
                <span>
                  <IconButton size="small" aria-label="Move section down" disabled={index === count - 1} onClick={() => onMove(1)}>
                    <ArrowDown size={16} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove section">
                <IconButton size="small" aria-label="Remove section" onClick={onRemove} data-cy="preship-section-remove">
                  <Trash2 size={16} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>

        {rows.length > 0 && (
          <Box sx={{ display: { xs: "none", md: "grid" }, gridTemplateColumns: GRID, gap: 1, px: 0.25 }}>
            {["Item", "Value", "Type"].map((h) => (
              <Typography key={h} sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary" }}>
                {h}
              </Typography>
            ))}
          </Box>
        )}

        {rows.map((row, i) => (
          <Box key={row.id} data-cy="preship-row" sx={{ display: "grid", gridTemplateColumns: GRID, gap: 1, alignItems: "start" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <TextField
                size="small"
                fullWidth
                disabled={!editable}
                value={row.label}
                placeholder="Item"
                onChange={(e) => setRow({ ...row, label: e.target.value })}
                inputProps={{ "aria-label": "Item", "data-cy": "preship-row-label" }}
              />
              <HelpTextButton row={row} editable={editable} onChange={(description) => setRow({ ...row, description })} />
            </Box>
            <PreShipValueInput
              row={row}
              disabled={!editable}
              serverError={serverErrors[row.id]}
              onChange={(value) => setRow({ ...row, value })}
            />
            <TextField
              select
              size="small"
              disabled={!editable || row.added_by === "customer"}
              value={typeOf(row)}
              onChange={(e) => {
                const type = e.target.value as PreShipValueType
                setRow({ ...row, type, choices: type === "choice" ? (row.choices ?? []) : [] })
              }}
              inputProps={{ "aria-label": `Type: ${row.label}`, "data-cy": "preship-row-type" }}
            >
              {VALUE_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", minHeight: 40 }}>
              {row.added_by === "customer" && <Chip size="small" color="info" variant="outlined" label="Customer" sx={{ height: 20, fontSize: 11, mr: 0.5 }} />}
              {editable && (
                <>
                  <IconButton size="small" aria-label="Move line up" disabled={i === 0} onClick={() => onChange({ ...section, rows: move(rows, i, -1) })}>
                    <ArrowUp size={14} />
                  </IconButton>
                  <IconButton size="small" aria-label="Move line down" disabled={i === rows.length - 1} onClick={() => onChange({ ...section, rows: move(rows, i, 1) })}>
                    <ArrowDown size={14} />
                  </IconButton>
                  <IconButton size="small" aria-label="Remove line" onClick={() => onChange({ ...section, rows: rows.filter((r) => r.id !== row.id) })}>
                    <Trash2 size={14} />
                  </IconButton>
                </>
              )}
            </Box>
            {typeOf(row) === "choice" && (
              <Autocomplete
                multiple
                freeSolo
                disabled={!editable}
                options={[]}
                value={row.choices ?? []}
                onChange={(_, next) => setRow({ ...row, choices: Array.from(new Set(next.map((c) => c.trim()).filter(Boolean))) })}
                sx={{ gridColumn: "1 / -1" }}
                renderInput={(params) => {
                  // AutocompleteRenderInputParams doesn't line up with TextFieldProps under
                  // exactOptionalPropertyTypes — narrow it (valid at runtime), as multi-user-selector does.
                  const field = params as TextFieldProps
                  return (
                    <TextField
                      {...field}
                      size="small"
                      placeholder={(row.choices ?? []).length ? "" : "Type an allowed value and press Enter"}
                      helperText={`Allowed values for “${row.label || "this line"}” — the customer picks one.`}
                      inputProps={{ ...params.inputProps, "aria-label": `Allowed values: ${row.label}`, "data-cy": "preship-row-choices" }}
                    />
                  )
                }}
              />
            )}
          </Box>
        ))}

        {rows.length === 0 && <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No lines in this section yet.</Typography>}

        {editable && (
          <Box>
            <Button size="small" startIcon={<Plus size={14} />} onClick={() => onChange({ ...section, rows: [...rows, newLine()] })} data-cy="preship-add-line">
              Add a line
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * The ⓘ beside an item: its help text as a tooltip, and — for staff who may
 * edit — a click opens a box to write it. Customers see the same text as a
 * tooltip in the support portal (it used to be the Description column).
 */
function HelpTextButton({ row, editable, onChange }: { row: PreShipLine; editable: boolean; onChange: (text: string) => void }): JSX.Element | null {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const help = row.description.trim()
  if (!editable && !help) return null

  return (
    <>
      <Tooltip title={help || "Add help text"}>
        <IconButton
          size="small"
          aria-label={help ? `Help: ${help}` : "Add help text"}
          data-cy="preship-row-help"
          color={help ? "primary" : "default"}
          onClick={editable ? (e) => setAnchor(e.currentTarget) : undefined}
          sx={{ opacity: help ? 1 : 0.5 }}
        >
          <Info size={16} />
        </IconButton>
      </Tooltip>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Box sx={{ p: 2, width: 340, display: "grid", gap: 1 }}>
          <TextField
            autoFocus
            multiline
            minRows={3}
            size="small"
            label="Help text"
            value={row.description}
            onChange={(e) => onChange(e.target.value)}
            inputProps={{ "data-cy": "preship-row-description", maxLength: 4000 }}
          />
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>The customer sees this when they point at the ⓘ next to the item.</Typography>
        </Box>
      </Popover>
    </>
  )
}
